import React, { useState, useEffect } from 'react';
import { Video, Phone, PhoneOff, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { notificationService } from '@/services/notificationService';
import { NotificationType, Notification } from '@/lib/types/notifications';
import { ProfileImage } from '../ui/ProfileImage';
import { toast } from 'react-hot-toast';

interface VideoChatNotificationBubbleProps {
  className?: string;
}

interface VideoInviteNotification extends Notification {
  data: {
    sessionId: string;
    workspaceId: string;
    invitedBy: string;
    invitedByName: string;
    sessionType: string;
  };
}

export const VideoChatNotificationBubble: React.FC<VideoChatNotificationBubbleProps> = ({
  className = ''
}) => {
  const [videoInvites, setVideoInvites] = useState<VideoInviteNotification[]>([]);
  const [currentInvite, setCurrentInvite] = useState<VideoInviteNotification | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser?.email) {
      return;
    }

    // Listen to notifications for video chat invites
    const unsubscribe = notificationService.listenToUserNotifications(
      currentUser.email,
      (notifications) => {
        const videoInvites = notifications.filter(
          (notification): notification is VideoInviteNotification =>
            notification.type === NotificationType.VideoChatInvite && !notification.read
        );

        setVideoInvites(videoInvites);

        // Show the most recent invite
        if (videoInvites.length > 0 && !currentInvite) {
          setCurrentInvite(videoInvites[0]);
          setShowNotification(true);
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [currentUser?.email, currentInvite]);

  const handleAcceptInvite = async () => {
    if (!currentInvite) return;

    try {
      // Mark notification as read
      await notificationService.markAsRead(currentInvite.id);
      
      // Navigate to brainstorm page with workspace and auto-join video
      navigate(`/brainstorm?workspace=${currentInvite.data.workspaceId}&joinVideo=true&sessionId=${currentInvite.data.sessionId}`);
      
      // Close notification
      handleCloseNotification();
      
      toast.success('Joining video session...');
    } catch (error) {
      console.error('Error accepting video invite:', error);
      toast.error('Failed to join video session');
    }
  };

  const handleDeclineInvite = async () => {
    if (!currentInvite) return;

    try {
      // Mark notification as read
      await notificationService.markAsRead(currentInvite.id);
      
      // Close notification
      handleCloseNotification();
      
      toast.success('Video invite declined');
    } catch (error) {
      console.error('Error declining video invite:', error);
      toast.error('Failed to decline invite');
    }
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
    setCurrentInvite(null);
    
    // Show next invite if available
    const remainingInvites = videoInvites.filter(invite => invite.id !== currentInvite?.id);
    if (remainingInvites.length > 0) {
      setTimeout(() => {
        setCurrentInvite(remainingInvites[0]);
        setShowNotification(true);
      }, 500);
    }
  };

  if (!showNotification || !currentInvite) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 50 }}
        className={`fixed bottom-6 right-6 z-[9999] ${className}`}
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden max-w-sm">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 text-white relative">
            <button
              onClick={handleCloseNotification}
              className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Video className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Video Chat Invitation</h3>
                <p className="text-blue-100 text-xs">Brainstorm Session</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <ProfileImage
                src={undefined} // We don't have photo URLs in the notification data
                name={currentInvite.data.invitedByName}
                size="md"
                className="w-10 h-10"
              />
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm">
                  {currentInvite.data.invitedByName}
                </p>
                <p className="text-gray-600 text-xs">
                  wants to start a video session
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-gray-700 text-sm">
                {currentInvite.message}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleDeclineInvite}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
              >
                <PhoneOff className="w-4 h-4" />
                Decline
              </button>
              <button
                onClick={handleAcceptInvite}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <Phone className="w-4 h-4" />
                Join
              </button>
            </div>
          </div>

          {/* Pulse Animation */}
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl opacity-20 animate-pulse -z-10"></div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
